import { useCallback, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import DualSliderInput from 'LuxKit/Slider/DualSliderInput'
import Group from 'components/utils/Group'
import debounce from 'lodash.debounce'
import { convertMinutesToTimeString } from 'lib/flights/flightUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FlightsFilterSegmentedControl from './FlightsFilterSegmentedControl'

interface Props {
  onFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  selectedDepartureMinItem?: string
  selectedDepartureMaxItem?: string
  selectedOutboundArrivalMinItem?: string
  selectedOutboundArrivalMaxItem?: string
  selectedReturnDepartureMinItem?: string
  selectedReturnDepartureMaxItem?: string
  selectedArrivalMinItem?: string
  selectedArrivalMaxItem?: string
  departureAirportCode: string
  arrivalAirportCode: string
  departureTimeItems: Array<FilterPanelCheckItem>
  /**
   * Outbound-leg arrival times for return searches.
   * Defaults to `arrivalTimeItems` for one-way filters.
   */
  outboundArrivalTimeItems?: Array<FilterPanelCheckItem>
  returnDepartureTimeItems?: Array<FilterPanelCheckItem>
  arrivalTimeItems: Array<FilterPanelCheckItem>
  shouldRenderCombinedTiles?: boolean
}

const STEP_NUM = 30

function getSliderBounds(items: Array<FilterPanelCheckItem>) {
  const minNum = (items[0]?.value as number) || 0
  const maxNum = (items[items.length - 1]?.value as number) || 1440
  const min = Math.floor(minNum / STEP_NUM) * STEP_NUM
  const rawMax = Math.ceil(maxNum / STEP_NUM) * STEP_NUM
  const max = rawMax <= min ? min + STEP_NUM : rawMax

  return { min, max }
}

function useSliderState(
  selectedMinItem: string | undefined,
  selectedMaxItem: string | undefined,
  items: Array<FilterPanelCheckItem>,
) {
  const { min, max } = useMemo(() => getSliderBounds(items), [items])
  const [startValue, setStartValue] = useState(
    selectedMinItem ? parseInt(selectedMinItem) : min,
  )
  const [endValue, setEndValue] = useState(
    selectedMaxItem ? parseInt(selectedMaxItem) : max,
  )

  useEffect(() => {
    if (!selectedMinItem) {
      setStartValue(min)
    }
  }, [selectedMinItem, min])

  useEffect(() => {
    if (!selectedMaxItem) {
      setEndValue(max)
    }
  }, [selectedMaxItem, max])

  return {
    min,
    max,
    startValue,
    endValue,
    setStartValue,
    setEndValue,
  }
}

interface TimeLegSlidersProps {
  onFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  departureAirportCode: string
  arrivalAirportCode: string
  departureMinName: string
  arrivalMinName: string
  selectedDepartureMinItem?: string
  selectedDepartureMaxItem?: string
  selectedArrivalMinItem?: string
  selectedArrivalMaxItem?: string
  departureTimeItems: Array<FilterPanelCheckItem>
  arrivalTimeItems: Array<FilterPanelCheckItem>
}

/**
 * Owns slider state for one visible leg only so inactive Departure/Return tabs
 * do not keep unused useSliderState hooks alive.
 */
function TimeLegSliders(props: TimeLegSlidersProps) {
  const {
    onFilterChange,
    departureAirportCode,
    arrivalAirportCode,
    departureMinName,
    arrivalMinName,
    selectedDepartureMinItem,
    selectedDepartureMaxItem,
    selectedArrivalMinItem,
    selectedArrivalMaxItem,
    departureTimeItems,
    arrivalTimeItems,
  } = props

  const intl = useIntl()

  const onChange = useMemo(() => {
    return debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange(e)
    }, 200)
  }, [onFilterChange])

  const departureSlider = useSliderState(
    selectedDepartureMinItem,
    selectedDepartureMaxItem,
    departureTimeItems,
  )
  const arrivalSlider = useSliderState(
    selectedArrivalMinItem,
    selectedArrivalMaxItem,
    arrivalTimeItems,
  )

  const handleDepartureStartChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    departureSlider.setStartValue(e.target.valueAsNumber)
    onChange(e)
  }, [departureSlider, onChange])

  const handleDepartureEndChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    departureSlider.setEndValue(e.target.valueAsNumber)
    onChange(e)
  }, [departureSlider, onChange])

  const handleArrivalStartChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    arrivalSlider.setStartValue(e.target.valueAsNumber)
    onChange(e)
  }, [arrivalSlider, onChange])

  const handleArrivalEndChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    arrivalSlider.setEndValue(e.target.valueAsNumber)
    onChange(e)
  }, [arrivalSlider, onChange])

  return (
    <Group direction="vertical" gap={16}>
      <Group direction="vertical" gap={8}>
        <BodyText variant="medium">
          {intl.formatMessage(
            {
              id: 'kd-M2jGH353IKzikPC5G',
              defaultMessage: 'Departing from {airportCode}',
              description: 'Heading for the departure time range slider in the flight search filter panel, showing the airport code',
            },
            { airportCode: departureAirportCode },
          )}
        </BodyText>

        <DualSliderInput
          variant="label"
          name={departureMinName}
          min={departureSlider.min}
          max={departureSlider.max}
          step={STEP_NUM}
          startValue={departureSlider.startValue}
          endValue={departureSlider.endValue}
          onStartChange={handleDepartureStartChange}
          onEndChange={handleDepartureEndChange}
          format="custom"
          formatValue={convertMinutesToTimeString}
        />
      </Group>

      <Group direction="vertical" gap={8}>
        <BodyText variant="medium">
          {intl.formatMessage(
            {
              id: 'Cpb36Idevu3FlOG6HjuG',
              defaultMessage: 'Arriving at {airportCode}',
              description: 'Heading for the arrival time range slider in the redesigned flight search filter panel',
            },
            { airportCode: arrivalAirportCode },
          )}
        </BodyText>

        <DualSliderInput
          variant="label"
          name={arrivalMinName}
          min={arrivalSlider.min}
          max={arrivalSlider.max}
          step={STEP_NUM}
          startValue={arrivalSlider.startValue}
          endValue={arrivalSlider.endValue}
          onStartChange={handleArrivalStartChange}
          onEndChange={handleArrivalEndChange}
          format="custom"
          formatValue={convertMinutesToTimeString}
        />
      </Group>
    </Group>
  )
}

function FlightSingleFilterTime(props: Props) {
  const intl = useIntl()
  const {
    onFilterChange,
    selectedDepartureMinItem,
    selectedDepartureMaxItem,
    selectedOutboundArrivalMinItem,
    selectedOutboundArrivalMaxItem,
    selectedReturnDepartureMinItem,
    selectedReturnDepartureMaxItem,
    selectedArrivalMinItem,
    selectedArrivalMaxItem,
    departureAirportCode,
    arrivalAirportCode,
    departureTimeItems,
    outboundArrivalTimeItems,
    returnDepartureTimeItems = [],
    arrivalTimeItems,
    shouldRenderCombinedTiles,
  } = props

  const resolvedOutboundArrivalTimeItems = outboundArrivalTimeItems ?? arrivalTimeItems

  const [activeTab, setActiveTab] = useState<'departure' | 'return'>('departure')

  const departureTabLabel = intl.formatMessage({
    id: 'YBGIX1J7wqJLJvy5kxY9',
    defaultMessage: 'Departure',
    description: 'Tab label for departure leg time filters in the redesigned flights filter panel',
  })
  const returnTabLabel = intl.formatMessage({
    id: '_yXFplxizDDSmKlbYd9A',
    defaultMessage: 'Return',
    description: 'Tab label for return leg time filters in the redesigned flights filter panel',
  })

  // Only combined tiles show both legs at once, so Departure/Return tabs only
  // make sense there. Classic single-tile return filters the active segment only.
  const showReturnTab = Boolean(shouldRenderCombinedTiles)
  const viewingReturnLeg = showReturnTab && activeTab === 'return'
  const useSeparateReturnFilters = viewingReturnLeg

  const segmentedOptions = useMemo(() => ([
    { value: 'departure', label: departureTabLabel },
    { value: 'return', label: returnTabLabel },
  ]), [departureTabLabel, returnTabLabel])

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as 'departure' | 'return')
  }, [])

  const outboundArrivalName = showReturnTab ? 'minOutboundArrivalTime' : 'minArrivalTime'

  return (
    <Group direction="vertical" gap={16}>
      {showReturnTab && (
        <FlightsFilterSegmentedControl
          inputsName="flight-time-leg"
          value={activeTab}
          options={segmentedOptions}
          onChange={handleTabChange}
        />
      )}

      {useSeparateReturnFilters ? (
        <TimeLegSliders
          key="return"
          onFilterChange={onFilterChange}
          departureAirportCode={arrivalAirportCode}
          arrivalAirportCode={departureAirportCode}
          departureMinName="minReturnDepartureTime"
          arrivalMinName="minArrivalTime"
          selectedDepartureMinItem={selectedReturnDepartureMinItem}
          selectedDepartureMaxItem={selectedReturnDepartureMaxItem}
          selectedArrivalMinItem={selectedArrivalMinItem}
          selectedArrivalMaxItem={selectedArrivalMaxItem}
          departureTimeItems={returnDepartureTimeItems}
          arrivalTimeItems={arrivalTimeItems}
        />
      ) : (
        <TimeLegSliders
          key="departure"
          onFilterChange={onFilterChange}
          departureAirportCode={departureAirportCode}
          arrivalAirportCode={arrivalAirportCode}
          departureMinName="minDepartureTime"
          arrivalMinName={outboundArrivalName}
          selectedDepartureMinItem={selectedDepartureMinItem}
          selectedDepartureMaxItem={selectedDepartureMaxItem}
          selectedArrivalMinItem={showReturnTab ? selectedOutboundArrivalMinItem : selectedArrivalMinItem}
          selectedArrivalMaxItem={showReturnTab ? selectedOutboundArrivalMaxItem : selectedArrivalMaxItem}
          departureTimeItems={departureTimeItems}
          arrivalTimeItems={resolvedOutboundArrivalTimeItems}
        />
      )}
    </Group>
  )
}

export default FlightSingleFilterTime
